import React from 'react';
import {
  Container,
  Grid,
  Alert,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { connect } from 'react-redux';

import {
  StatCard,
  FilterBar,
  AnalyticsErrorBoundary,
  DailySalesTrendChart,
  GSTTrendChart,
  SalesGrowthChart,
  PaymentModeChart,
  MostOrderedItemsChart,
  RevenueByCategoryChart,
  useAnalyticsData,
} from './components';
import type { Report } from './components';

interface AnalyticsProps {
  reports: Report[];
  isSuperAdmin: boolean;
}

const Analytics: React.FC<AnalyticsProps> = ({ reports, isSuperAdmin }) => {
  const theme = useTheme();

  const {
    startDate,
    endDate,
    serviceType,
    gstFilter,
    setStartDate,
    setEndDate,
    setServiceType,
    setGstFilter,
    loading,
    error,
    retry,
    stats,
    trendData,
    gstTrendData,
  } = useAnalyticsData(reports, isSuperAdmin);

  // ----- Loading state -----
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // ----- Error state -----
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" color="primary" onClick={retry}>
          Retry
        </Button>
      </Container>
    );
  }

  // ----- Main render -----
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2.5, md: 4 }, px: { xs: 1.5, md: 3 }, mb: 4 }}>
      <Grid container spacing={2.5}>
        {/* Filters */}
        <Grid item xs={12}>
          <FilterBar
            startDate={startDate}
            endDate={endDate}
            serviceType={serviceType}
            gstFilter={gstFilter}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onServiceTypeChange={setServiceType}
            onGstFilterChange={setGstFilter}
            isSuperAdmin={isSuperAdmin}
          />
        </Grid>

        <AnalyticsErrorBoundary>
          {/* Stat cards */}
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Sales"
              value={`₹${stats.totalSales.toLocaleString()}`}
              icon={<TrendingUpIcon sx={{ color: theme.palette.primary.main }} />}
              trend={12.5}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={serviceType === 'room' ? 'Total Bookings' : 'Total Orders'}
              value={stats.totalOrders}
              icon={<TrendingUpIcon sx={{ color: theme.palette.success.main }} />}
              trend={8.2}
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title={serviceType === 'room' ? 'Total Rooms' : 'Total Items'}
              value={stats.totalItems}
              icon={<TrendingUpIcon sx={{ color: theme.palette.warning.main }} />}
              trend={5.7}
              color={theme.palette.warning.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Order Value"
              value={`₹${stats.averageOrderValue.toLocaleString()}`}
              icon={<TrendingUpIcon sx={{ color: theme.palette.info.main }} />}
              trend={2.4}
              color={theme.palette.info.main}
            />
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <DailySalesTrendChart data={stats.salesOverTime} />
          </Grid>
          <Grid item xs={12} md={6}>
            <GSTTrendChart data={gstTrendData} />
          </Grid>
          <Grid item xs={12} md={6}>
            <SalesGrowthChart data={trendData} />
          </Grid>
          <Grid item xs={12} md={6}>
            <PaymentModeChart data={stats.paymentModeData} />
          </Grid>
          <Grid item xs={12} md={6}>
            <MostOrderedItemsChart data={stats.mostOrderedItems} serviceType={serviceType} />
          </Grid>
          <Grid item xs={12} md={6}>
            <RevenueByCategoryChart data={stats.categoryData} serviceType={serviceType} />
          </Grid>
        </AnalyticsErrorBoundary>
      </Grid>
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  reports: state.common?.ordersReport ?? [],
  isSuperAdmin: !!(state.user?.user?.isSuperAdmin),
});

export default connect(mapStateToProps)(Analytics);
